import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ActionTabs,
  type ActionTabItem,
  type AppIcons,
  NUGGET_VARIANTS,
} from '@backrs_ui/common-ui';

import { ShareResourceModal } from '@backrs_ui/grow-ui/src/components/ShareResourceModal/ShareResourceModal';
import { isStructuredSession } from '@backrs_ui/grow-ui/src/models/models';
import {
  useDeleteBookmarkMutation,
  useGetBookmarksQuery,
  usePostBookmarkMutation,
} from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getDisplayedSession,
  getSessionState,
  isShellManagedSession,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import { getSessionStatusEntry } from '@backrs_ui/grow-ui/src/redux/sessionStatuses/sessionStatuses';

import { Box } from '@mui/material';

import { getBookmarkIdForNugget } from '../../pages/Bookmarks/pages/bookmarks.util';
import { StickerCardId, useToolCardById } from '../../pages/Home/hooks/stickerCardMapping';
import { useGetTaskActionsAndProperties } from '../../pages/Priorities/hooks/tasks.hooks';

export const StructuredSessionActionButtons = () => {
  const navigate = useNavigate();

  const activeSession = useAppSelector(getDisplayedSession);
  const sessionId = useAppSelector((state) => {
    if (!activeSession) return null;

    // A previewed session isn't in session state yet, so read its id from the per-type status map.
    const statusSessionId = isStructuredSession(activeSession)
      ? getSessionStatusEntry(state, activeSession)?.sessionId
      : undefined;

    return getSessionState(state, activeSession).coachLubav.sessionId ?? statusSessionId ?? null;
  });

  const { getStickerCard } = useToolCardById({ navigate });
  const { openToolNuggetAsTask } = useGetTaskActionsAndProperties();

  const { data: bookmarks } = useGetBookmarksQuery(undefined);
  const [postBookmark] = usePostBookmarkMutation();
  const [deleteBookmark] = useDeleteBookmarkMutation();

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  if (!activeSession || !isShellManagedSession(activeSession)) {
    return null;
  }

  const stickerCard = getStickerCard(activeSession as StickerCardId);
  const tool = stickerCard?.tool;

  if (!tool) {
    return null;
  }

  const bookmarkId = getBookmarkIdForNugget(bookmarks, tool.id, NUGGET_VARIANTS.TOOL);

  const handleBookmark = async () => {
    if (bookmarkId) {
      await deleteBookmark(bookmarkId);
    } else {
      await postBookmark({ type: 'tool', itemId: tool.id });
    }
  };

  const handleShare = () => {
    if (!sessionId) {
      return;
    }

    setIsShareModalOpen(true);
  };

  const actions: ActionTabItem[] = [
    {
      icon: (bookmarkId ? 'BookmarkCheck' : 'Bookmark') as AppIcons,
      onClick: handleBookmark,
      tooltipHoverText: 'Bookmark',
    },
    {
      icon: 'Share06' as const,
      onClick: handleShare,
      tooltipHoverText: 'Share',
      disabled: !sessionId,
    },
    {
      icon: 'BookmarkAdd' as const,
      onClick: () => {
        if (!stickerCard) {
          return;
        }

        openToolNuggetAsTask(stickerCard);
      },
      tooltipHoverText: 'Add Task',
    },
  ];

  return (
    <>
      <Box sx={{ '& .MuiTabs-root': { minHeight: 40 } }}>
        <ActionTabs actions={actions} borderColor="Colors/Border/border-primary" />
      </Box>
      {sessionId && (
        <ShareResourceModal
          open={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          resourceType="office_hours"
          resourceId={sessionId}
          sharePath={`/office-hours/${activeSession}`}
          resourceName={stickerCard?.title ?? 'Session'}
        />
      )}
    </>
  );
};
